<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue';
import { t } from './i18n';
import { buildItemSvg } from './engine';
import { ensureItemFonts } from './fontEngine';
import { normalizeBadge, normalizeTypography, TYPOGRAPHY_BADGE_FRAME, withCurrentCurves } from './config';
import type { TypographyDesignItem } from './types';

const props = withDefaults(defineProps<{
  kind: 'text' | 'badge';
  config: Record<string, unknown>;
  title?: string;
  compact?: boolean;
  sampleText?: string;
}>(), {
  title: '',
  compact: false,
  sampleText: '',
});
const state = ref<'loading' | 'ready' | 'unavailable'>('loading');
const svg = ref('');
const previewHost = ref<HTMLElement | null>(null);
let requestRevision = 0;

async function fitPreviewSvg(revision: number): Promise<void> {
  await nextTick();
  if (revision !== requestRevision) return;
  const node = previewHost.value?.querySelector('svg');
  if (!node) return;
  node.setAttribute('width', '100%');
  node.setAttribute('height', '100%');
  node.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  node.style.display = 'block';
}

const previewItem = computed<TypographyDesignItem>(() => {
  const frame = props.kind === 'badge' ? { ...TYPOGRAPHY_BADGE_FRAME } : { width: 360, height: 180 };
  if (props.kind === 'badge') {
    return { id: 'admin-badge-preview', type: 'shape', width: frame.width, height: frame.height, typographyFrame: frame, typographyScale: 1, typographyBadge: withCurrentCurves(normalizeBadge(props.config)) };
  }
  const font = normalizeTypography(props.config).font;
  const text = (props.sampleText || 'Typography Pro').trim() || 'Typography Pro';
  return { id: 'admin-text-preview', type: 'text', text, width: frame.width, height: frame.height, fontFamily: font?.family ?? 'Roboto', fontWeight: font?.weight ?? '700', fontStyle: 'normal', fontSize: 56, color: '#111827', opacity: 1, typographyFrame: frame, typographyScale: 1, typography: withCurrentCurves(normalizeTypography(props.config)) };
});

watch(previewItem, async (item) => {
  const revision = ++requestRevision;
  state.value = 'loading';
  svg.value = '';
  try {
    const ready = await ensureItemFonts(item);
    if (revision !== requestRevision) return;
    const output = ready ? buildItemSvg(item) : '';
    svg.value = output;
    state.value = output ? 'ready' : 'unavailable';
    if (output) await fitPreviewSvg(revision);
  } catch (error) {
    // A font that cannot be decoded (e.g. a RangeError from fontkit) must not reach the
    // admin error boundary, which would show it as a toast. Fall back to the placeholder.
    if (revision !== requestRevision) return;
    console.warn('[typography-pro] preview failed', props.title, item.fontFamily, error);
    svg.value = '';
    state.value = 'unavailable';
  }
}, { immediate: true, deep: true });
</script>

<template>
  <div
    class="vptw:flex vptw:items-center vptw:justify-center vptw:overflow-hidden vptw:bg-[radial-gradient(circle_at_center,#fff_0,#f8fafc_70%)]"
    :class="compact ? 'vptw:h-28 vptw:rounded-lg' : 'vptw:h-72 vptw:rounded-2xl vptw:border vptw:border-(--vs-line)'">
    <div ref="previewHost" v-if="state === 'ready' && svg" class="vptw:h-full vptw:w-full" v-html="svg" />
    <div v-else-if="state === 'loading'" class="vptw:flex vptw:items-center vptw:gap-2 vptw:text-xs vptw:text-(--vs-ink-3)">
      <span
        class="vptw:size-3 vptw:animate-spin vptw:rounded-full vptw:border-2 vptw:border-(--vs-line) vptw:border-t-indigo-500" />
      {{ t('Preparing preview') }}</div>
    <div v-else
      class="vptw:flex vptw:flex-col vptw:items-center vptw:gap-1 vptw:px-3 vptw:text-center vptw:text-xs vptw:text-amber-700">
      <span class="vptw:text-lg">Aa</span><span>{{ t('Preview font unavailable') }}</span></div>
  </div>
</template>
